'use client';

import type { Household } from 'src/types/household';
import { Text } from '@savvy-kit/foundations/text';
import { isClient } from 'src/utils/advisor-dash/household';
import { convertSnakeCaseToSentenceCase } from 'src/utils/url';

import { formatLargeDollarAmount, formatTimestampToRelative } from '../../../utils/formatting';
import { StageIcon } from '../prospect-detail/prospect-stage-icons';

export type HouseholdSearchResultProps = {
  readonly household: Household;
};

export function HouseholdSearchResult({ household }: HouseholdSearchResultProps) {
  const isClientHousehold = isClient(household.householdStage);
  const stage = convertSnakeCaseToSentenceCase(household.householdStage || '');

  return (
    <div className="flex flex-col gap-2 w-full">
      <Text variant="subtitle-sm">{household.name}</Text>
      <div className="grid grid-cols-12">
        <div className="col-span-4">
          <div className="flex flex-col gap-0">
            <Text variant="caption" color="secondary">
              {isClientHousehold ? 'Client revenue' : 'Stage'}
            </Text>
            {(() => {
              if (isClientHousehold) {
                return (
                  <Text variant="body-sm" color="dark">
                    {Number(household.advisorAnnualRevenue) > 0
                      ? formatLargeDollarAmount(Number(household.advisorAnnualRevenue))
                      : '–'}
                  </Text>
                );
              }

              if (household.householdStage) {
                return (
                  <div className="flex flex-col gap-1">
                    <Text variant="body-sm" color="dark">
                      {stage}
                    </Text>
                    <StageIcon stageName={stage} />
                  </div>
                );
              }

              return (
                <Text variant="body-sm" color="dark">
                  –
                </Text>
              );
            })()}
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-col gap-0">
            <Text variant="caption" color="secondary">
              {isClientHousehold ? 'Billable Assets' : 'Est. AUM'}
            </Text>
            {isClientHousehold && (
              <Text variant="body-sm" color="dark">
                {Number(household.savvyAum) > 0 ? formatLargeDollarAmount(Number(household.savvyAum)) : '–'}
              </Text>
            )}
            {!isClientHousehold && (
              <Text variant="body-sm" color="dark">
                {household.prospectData?.estimatedAum
                  ? formatLargeDollarAmount(Number(household.prospectData.estimatedAum))
                  : '–'}
              </Text>
            )}
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-col gap-0">
            <Text variant="caption" color="secondary">
              Last contact
            </Text>
            <Text variant="body-sm" color="dark">
              {household.lastContactAt ? formatTimestampToRelative(household.lastContactAt) : '–'}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
